// The conversation on assistant-ui (design-revamp.md section 5): our reducer is the store,
// assistant-ui draws it. Answers render as markdown; tool calls as cards; the person's message shows
// the moment Send is pressed (section 6). A time divider goes before a message after a gap, and each
// message's exact time shows on hover, or on tap on a touch screen. Where attachments are offered,
// files are added with the paperclip, by drop or by paste, and upload at once (attachments.md section 8).
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
  useExternalStoreRuntime,
  type AppendMessage,
  type ThreadMessageLike,
} from "@assistant-ui/react";
import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import { ArrowUp, Loader2, Paperclip, Square, X } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import remarkGfm from "remark-gfm";
import type { AttachmentRef, DriveUploads } from "../attachments.js";
import { isAlphaLabUrl } from "../links.js";
import { exactTime } from "../times.js";
import { ComposerAttachment, SentAttachment, UploadsContext } from "./Attachments.js";
import { Start } from "./Start.js";
import { Button } from "./ui/button.js";
import { ToolCard } from "./ToolCard.js";

/** How a link opens: in the AlphaLab panel when it can, else a new tab (section 8). */
export const OpenLinkContext = createContext<(url: string, title?: string) => boolean>(() => false);

function Link({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const open = useContext(OpenLinkContext);
  return (
    <a
      {...rest}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-ink underline underline-offset-2"
      onClick={(e) => {
        if (href && isAlphaLabUrl(href) && open(href)) e.preventDefault();
      }}
    >
      {children}
    </a>
  );
}

function MarkdownText() {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md py-1.5 text-[15px] leading-[1.7]"
      components={{ a: Link }}
    />
  );
}

/**
 * A message with its time (thread.ts MessageTime): the divider before it, the exact time as its
 * tooltip, and on a touch screen, where there is no hover, a tap shows it below.
 */
function Timed({ className, children }: { className: string; children: ReactNode }) {
  const at = useAuiState((s) => s.message.metadata.custom.at) as number | undefined;
  const divider = useAuiState((s) => s.message.metadata.custom.divider) as string | undefined;
  const [shown, setShown] = useState(false);
  const exact = at ? exactTime(at) : undefined;
  const onClick = (e: MouseEvent) => {
    if (!exact || !window.matchMedia?.("(hover: none)").matches) return;
    // A tap on a link, a button or a tool card's details is theirs.
    if ((e.target as Element).closest("a, button, details")) return;
    setShown((v) => !v);
  };
  return (
    <>
      {divider && <p className="pt-4 pb-1 text-center text-xs text-muted-foreground">{divider}</p>}
      <MessagePrimitive.Root className={className} title={exact} onClick={onClick}>
        {children}
        {shown && exact && <p className="mt-1 text-xs text-muted-foreground">{exact}</p>}
      </MessagePrimitive.Root>
    </>
  );
}

function UserMessage() {
  // A message may carry only files.
  const hasText = useAuiState((s) => s.message.content.length > 0);
  return (
    <Timed className="flex flex-col items-end gap-1.5 py-3">
      <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5 empty:hidden">
        <MessagePrimitive.Attachments>{({ attachment }) => <SentAttachment attachment={attachment} />}</MessagePrimitive.Attachments>
      </div>
      {hasText && (
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-secondary px-4 py-2.5 text-[15px] leading-relaxed text-secondary-foreground">
          <MessagePrimitive.Parts />
        </div>
      )}
    </Timed>
  );
}

function AssistantMessage() {
  return (
    <Timed className="py-3">
      <div className="flex flex-col gap-1.5 text-foreground">
        <MessagePrimitive.Parts components={{ Text: MarkdownText, tools: { Fallback: ToolCard } }} />
      </div>
    </Timed>
  );
}

function SystemMessage() {
  return (
    <Timed className="flex flex-col items-center py-3 text-xs text-muted-foreground">
      <div className="flex w-full items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <MessagePrimitive.Parts />
        <span className="h-px flex-1 bg-border" />
      </div>
    </Timed>
  );
}

export interface ThreadProps {
  messages: ThreadMessageLike[];
  running: boolean;
  busy: boolean;
  /** The history has been read: with no messages, the start page shows (never while it loads). */
  loaded: boolean;
  thinking: string | undefined;
  /** Earlier history exists: a button loads it. */
  onLoadEarlier?: () => void;
  onSend(text: string, attachments: AttachmentRef[]): void;
  onStop(): void;
  /** Shown between the conversation and the composer: a notice, or the pending message's state. */
  status?: ReactNode;
  /** The composer's uploads, where attachments are offered and the grant has the Drive scopes. */
  uploads?: DriveUploads;
  /** Attachments are offered, but the grant lacks the Drive scopes: the paperclip says to reconnect. */
  reconnectToAttach?: boolean;
}

const noSubscribe = () => () => undefined;
const NO_NOTICES: readonly string[] = [];

export function Thread({ messages, running, busy, loaded, thinking, onLoadEarlier, onSend, onStop, status, uploads, reconnectToAttach }: ThreadProps) {
  const subscribe = uploads?.subscribe ?? noSubscribe;
  // A failed upload holds Send until it is retried or removed (A4); one still running, Send waits for.
  const blocked = useSyncExternalStore(subscribe, () => uploads?.blocked ?? false);
  const uploading = useSyncExternalStore(subscribe, () => uploads?.busy ?? false);
  const notices = useSyncExternalStore(subscribe, () => uploads?.notices ?? NO_NOTICES);
  const [askReconnect, setAskReconnect] = useState(false);
  const adapters = useMemo(() => (uploads ? { attachments: uploads } : undefined), [uploads]);

  // Leaving the page loses an upload in flight: the browser asks first.
  useEffect(() => {
    if (!uploading) return;
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [uploading]);

  const runtime = useExternalStoreRuntime<ThreadMessageLike>({
    messages,
    isRunning: running,
    isSendDisabled: busy || blocked,
    convertMessage: (m) => m,
    adapters,
    onNew: async (m: AppendMessage) => {
      const text = m.content
        .filter((p): p is { type: "text"; text: string } => p.type === "text")
        .map((p) => p.text)
        .join("\n")
        .trim();
      // Only files AlphaLab stored reach here: `send` refuses any other (A5).
      const files = uploads ? uploads.sent((m.attachments ?? []).map((a) => a.id)) : [];
      if (text || files.length) onSend(text, files);
    },
    onCancel: async () => onStop(),
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <UploadsContext.Provider value={uploads}>
        <ThreadPrimitive.Root className="flex h-full min-h-0 flex-col">
          <ThreadPrimitive.Viewport className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4" aria-live="polite">
            <div className="mx-auto w-full max-w-[var(--thread-max-width)] pt-4">
              {onLoadEarlier && (
                <div className="flex justify-center pb-2">
                  <Button variant="ghost" size="sm" onClick={onLoadEarlier}>
                    Show earlier messages
                  </Button>
                </div>
              )}
              {loaded && messages.length === 0 && !thinking && <Start />}
              <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage, SystemMessage }} />
              {thinking && (
                <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  {thinking}
                </p>
              )}
            </div>
          </ThreadPrimitive.Viewport>
          <div className="mx-auto w-full max-w-[var(--thread-max-width)] px-4 pb-4">
            {status}
            {(notices.length > 0 || askReconnect) && (
              <div className="mb-2 flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                <div className="min-w-0 flex-1 space-y-0.5 break-words">
                  {askReconnect && (
                    <p>
                      Reconnect AlphaLab to attach files.{" "}
                      <a className="font-medium underline" href="/api/auth/login">
                        Reconnect
                      </a>
                    </p>
                  )}
                  {notices.map((n, i) => (
                    <p key={i}>{n}</p>
                  ))}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  className="shrink-0 rounded p-0.5 hover:bg-accent"
                  onClick={() => {
                    uploads?.dismissNotices();
                    setAskReconnect(false);
                  }}
                >
                  <X className="size-4" />
                </button>
              </div>
            )}
            {/* The whole composer takes dropped files; the input takes pasted ones. */}
            <ComposerPrimitive.AttachmentDropzone className="rounded-xl outline-offset-2 data-[dragging=true]:outline-2 data-[dragging=true]:outline-dashed data-[dragging=true]:outline-ring">
              <ComposerPrimitive.Root className="flex flex-col gap-2 rounded-xl border border-input bg-card p-2 shadow-xs focus-within:border-ring">
                {uploads && (
                  <div className="flex min-w-0 flex-wrap items-start gap-1.5 empty:hidden">
                    <ComposerPrimitive.Attachments>{({ attachment }) => <ComposerAttachment attachment={attachment} />}</ComposerPrimitive.Attachments>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  {uploads && (
                    <ComposerPrimitive.AddAttachment asChild multiple>
                      <Button size="icon" variant="ghost" aria-label="Attach files" title="Attach files">
                        <Paperclip />
                      </Button>
                    </ComposerPrimitive.AddAttachment>
                  )}
                  {!uploads && reconnectToAttach && (
                    <Button size="icon" variant="ghost" aria-label="Reconnect AlphaLab to attach files" title="Reconnect AlphaLab to attach files" onClick={() => setAskReconnect(true)}>
                      <Paperclip className="opacity-50" />
                    </Button>
                  )}
                  <ComposerPrimitive.Input
                    aria-label="Message"
                    maxLength={20_000}
                    rows={1}
                    autoFocus
                    placeholder="Ask Quanty One about your research"
                    className="max-h-48 min-h-9 min-w-0 flex-1 resize-none bg-transparent px-2 py-1.5 text-[15px] outline-none placeholder:text-muted-foreground"
                  />
                  {running ? (
                    <ComposerPrimitive.Cancel asChild>
                      <Button size="icon" variant="outline" aria-label="Stop">
                        <Square className="size-3.5 fill-current" />
                      </Button>
                    </ComposerPrimitive.Cancel>
                  ) : (
                    <ComposerPrimitive.Send asChild>
                      <Button size="icon" aria-label="Send" title={blocked ? "Retry or remove the file that did not upload" : undefined}>
                        <ArrowUp />
                      </Button>
                    </ComposerPrimitive.Send>
                  )}
                </div>
              </ComposerPrimitive.Root>
            </ComposerPrimitive.AttachmentDropzone>
          </div>
        </ThreadPrimitive.Root>
      </UploadsContext.Provider>
    </AssistantRuntimeProvider>
  );
}
